import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../services/axiosinstance";
import { apiUrl } from "../routes/apiRoutes";
import { loginToPannelAPI } from "../services/apicollection";

// Hydrate non-sensitive admin info from localStorage on refresh.
// Cookie (validated via /v2/me) is still the source of truth.
const storedAdmin =
  typeof window !== "undefined" ? window.localStorage.getItem("admin") : null;

const initialUser = storedAdmin ? JSON.parse(storedAdmin) : null;

export const loginAdmin = createAsyncThunk(
  "auth/loginAdmin",
  async (credentials, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(apiUrl.AUTH_LOGIN, credentials);

      if (!res.data?.status) {
        return rejectWithValue(
          res.data?.message || "Invalid email or password",
        );
      }

      const adminInfo = res.data.data || null;
      if (adminInfo && typeof window !== "undefined") {
        window.localStorage.setItem("admin", JSON.stringify(adminInfo));
      }
      loginToPannelAPI();
      return adminInfo;
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Something went wrong while logging in.";
      return rejectWithValue(msg);
    }
  },
);

export const checkAuth = createAsyncThunk(
  "auth/checkAuth",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(apiUrl.AUTH_ME);

      if (!res.data?.status) {
        return rejectWithValue(res.data?.message || "Not authenticated");
      }

      return res.data.data;
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Not authenticated";
      return rejectWithValue(msg);
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: initialUser,
    loading: false,
    error: null,
  },
  reducers: {
    logout(state) {
      state.user = null;
      state.loading = false;
      state.error = null;
      if (typeof window !== "undefined") {
        window.localStorage.removeItem("admin");
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(loginAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Login failed";
        state.user = null;
      })
      .addCase(checkAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(checkAuth.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.error = null;
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
